import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { EmailTemplate, EmailTemplatesService, tagClassName, displayTag, tagActiveStyle } from "../services/emailTemplatesService";

const Templates: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTags = searchParams.get("tags")?.split(",").map(t => t.trim().toLowerCase()).filter(Boolean) ?? [];

  useEffect(() => {
    EmailTemplatesService.getTemplates()
      .then(setTemplates)
      .catch(() => setError("Failed to load templates."))
      .finally(() => setLoading(false));
  }, []);

  function toggleTag(tag: string) {
    const next = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    if (next.length > 0) {
      setSearchParams({ tags: next.join(",") });
    } else {
      setSearchParams({});
    }
  }

  if (loading) {
    return <div className="templates-page"><p className="templates-status">Loading templates...</p></div>;
  }

  if (error) {
    return <div className="templates-page"><p className="templates-status">{error}</p></div>;
  }

  const allTags = Array.from(new Set(templates.flatMap(t => t.tags.map(tag => tag.toLowerCase())))).sort();
  const visible = selectedTags.length > 0
    ? templates.filter(t => selectedTags.some(st => t.tags.map(tag => tag.toLowerCase()).includes(st)))
    : templates;

  return (
    <div className="templates-page">
      <div className="templates-page-header">
        <div>
          <h1>Templates</h1>
          <p style={{ marginTop: "0" }}>Pre-built email designs ready to be made yours.</p>
        </div>
        {allTags.length > 0 && (
          <div className="templates-tag-filter">
            {allTags.map(tag => (
              <button
                key={tag}
                className="templates-tag-btn"
                style={selectedTags.includes(tag) ? tagActiveStyle(tag) : undefined}
                onClick={() => toggleTag(tag)}
              >
                {displayTag(tag)}
              </button>
            ))}
          </div>
        )}
      </div>

      {visible.length === 0 && <p className="templates-status">No templates found.</p>}

      <div className="templates-grid">
        {visible.map((template) => (
          <Link
            key={template.id}
            className="template-card"
            to={`/templates/${template.id}`}
          >
            <div className="template-card-thumbnail">
              {template.preview_html ? (
                <div className="template-card-thumbnail-scaler">
                  <iframe
                    srcDoc={template.preview_html}
                    title={template.title}
                    sandbox="allow-same-origin"
                    className="template-card-thumbnail-iframe"
                  />
                </div>
              ) : (
                <span className="template-card-thumbnail-label">Preview →</span>
              )}
            </div>
            <div className="template-card-body">
              <h3 className="template-card-title">{template.title}</h3>
              {template.description && (
                <p className="template-card-description">{template.description}</p>
              )}
              {template.tags.length > 0 && (
                <div className="template-card-tags" style={{ marginTop: "auto" }}>
                  {template.tags.map(tag => (
                    <span key={tag} className={tagClassName(tag)}>{displayTag(tag)}</span>
                  ))}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default Templates;
